"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.addPinToList = exports.updatePinLayout = exports.updatePinComponentSettings = exports.overridePinComponents = exports.removeComponentLayoutPart = exports.updateComponentOrder = exports.updateComponentLayoutPart = exports.updateComponentLayout = exports.updatePinTeacherSettings = exports.updatePinSettings = exports.deletePinComponents = exports.addPinComponents = exports.reorderPins = exports.deletePins = exports.addPins = void 0;
const pins_shared_1 = require("@lessonup/pins-shared");
const lodash_1 = require("lodash");
const order_1 = require("./order");
const y_doc_fragment_helpers_1 = require("./y-doc-fragment-helpers");
/**
 * Adds pins to the current state, and updates the order if necessary
 * This function will mutate the state.pins array in place
 * The provided pins will be cloned before being added to the state
 */
function addPins(state, yDoc, pins, afterPinId, endOfPhase) {
    return yDoc.transact(() => {
        state.pins = state.pins || [];
        let afterNewPinId = afterPinId;
        pins.forEach((pin) => {
            const pinClone = (0, lodash_1.cloneDeep)(pin);
            const newPin = (0, order_1.refreshPinComponentOrders)(pinClone);
            (0, y_doc_fragment_helpers_1.moveTipTapContentToFragments)(yDoc, newPin);
            addPinToList(state, newPin, afterNewPinId, endOfPhase);
            afterNewPinId = newPin.id;
        });
        return state;
    });
}
exports.addPins = addPins;
/**
 * This function will mutate the state.pins array in place
 */
function deletePins(state, yDoc, pinIds) {
    return yDoc.transact(() => {
        const pinIdsToDeleteSet = new Set(pinIds);
        inPlaceArrayRemove(state.pins || [], (pin) => {
            const shouldDelete = pinIdsToDeleteSet.has(pin.id);
            if (shouldDelete) {
                (0, y_doc_fragment_helpers_1.deletePinFragments)(yDoc, pin);
            }
            return shouldDelete;
        });
        return state;
    });
}
exports.deletePins = deletePins;
/**
 * This function will mutate the state.pins array in place
 */
function reorderPins(state, yDoc, pinIds, startOfPhase, afterPinId) {
    return yDoc.transact(() => {
        internalReorderPins(state.pins, pinIds, startOfPhase, afterPinId);
        return state;
    });
}
exports.reorderPins = reorderPins;
/**
 * This function will mutate the pinComponents array in place
 * The provided pinComponents will be cloned before being added to the pin
 */
function addPinComponents(state, yDoc, pinId, pinComponents) {
    return yDoc.transact(() => updatePin(state, yDoc, pinId, (pin) => {
        const orders = (0, order_1.nextNPinComponentOrders)(pin, pinComponents.length);
        const newComponents = pinComponents.map((pc, i) => {
            const clone = (0, lodash_1.cloneDeep)(pc);
            clone.order = orders[i];
            return clone;
        });
        pin.pinComponents.push(...newComponents);
        return pin;
    }));
}
exports.addPinComponents = addPinComponents;
/**
 * This function will remove the provided pin components from the pin
 * References to them will also be removed from the linkedPinComponent settings
 * Any layout parts that reference the deleted components will be removed
 */
function deletePinComponents(state, yDoc, pinId, pinComponentIds) {
    return yDoc.transact(() => updatePin(state, yDoc, pinId, (pin) => {
        const idsSet = new Set(pinComponentIds);
        inPlaceArrayRemove(pin.pinComponents || [], (pinComponent) => {
            return idsSet.has(pinComponent.id) && (0, pins_shared_1.canDeletePinComponent)(pin, pinComponent);
        });
        return pin;
    }));
}
exports.deletePinComponents = deletePinComponents;
/**
 * The provided settings will be cloned before being assigned to the pin
 */
function updatePinSettings(state, yDoc, pinId, settings) {
    return yDoc.transact(() => updatePin(state, yDoc, pinId, (pin) => {
        const pinSettings = pin.settings;
        Object.keys(settings).forEach((key) => {
            pinSettings[key] = (0, lodash_1.cloneDeep)(settings[key]); // TODO: ED-305 make updates atomic - selective update component properties, not a full clone
        });
        return pin;
    }));
}
exports.updatePinSettings = updatePinSettings;
/**
 * The provided settings will be cloned before being assigned to the pin
 */
function updatePinTeacherSettings(state, yDoc, pinId, settings) {
    return yDoc.transact(() => updatePin(state, yDoc, pinId, (pin) => {
        const pinSettings = pin.teacherSettings;
        Object.keys(settings).forEach((key) => {
            pinSettings[key] = (0, lodash_1.cloneDeep)(settings[key]); // TODO: ED-305 make updates atomic - selective update component properties, not a full clone
        });
        return pin;
    }));
}
exports.updatePinTeacherSettings = updatePinTeacherSettings;
/**
 * The provided layout will be cloned before being assigned to the pin
 */
function updateComponentLayout(state, yDoc, pinId, updates) {
    return yDoc.transact(() => updatePin(state, yDoc, pinId, (pin) => {
        Object.entries(updates).forEach(([componentId, layout]) => {
            const component = pin.pinComponents.find((component) => component.id === componentId);
            if (component) {
                component.layout = (0, lodash_1.cloneDeep)(layout); // TODO: ED-305 make updates atomic - selective update component properties, not a full clone
            }
        });
        return pin;
    }));
}
exports.updateComponentLayout = updateComponentLayout;
function updateComponentLayoutPart(state, yDoc, pinId, pinComponentId, layoutPart) {
    return yDoc.transact(() => updatePin(state, yDoc, pinId, (pin) => {
        if (!(0, pins_shared_1.isPinWithLayoutParts)(pin))
            return pin;
        pin.settings.layoutParts[layoutPart] = pinComponentId;
        return pin;
    }));
}
exports.updateComponentLayoutPart = updateComponentLayoutPart;
function updateComponentOrder(state, yDoc, pinId, pinComponentIds, action) {
    return yDoc.transact(() => {
        updatePin(state, yDoc, pinId, (pin) => {
            (0, order_1.changePinComponentsOrder)(pin.pinComponents, pinComponentIds, action);
            return pin;
        });
        return state;
    });
}
exports.updateComponentOrder = updateComponentOrder;
// ToDo: can we remove this function?
function removeComponentLayoutPart(state, yDoc, pinId, pinComponentId) {
    return yDoc.transact(() => updatePin(state, yDoc, pinId, (pin) => {
        if (!(0, pins_shared_1.isPinWithLayoutParts)(pin))
            return pin;
        const layoutPart = Object.entries(pin.settings.layoutParts).find(([, id]) => id === pinComponentId);
        if (layoutPart) {
            delete pin.settings.layoutParts[layoutPart[0]];
        }
        return pin;
    }));
}
exports.removeComponentLayoutPart = removeComponentLayoutPart;
/**
 * The provided pinComponents will be cloned before being assigned to the pin
 */
function overridePinComponents(state, yDoc, pinId, pinComponents) {
    if (pinComponents.length === 0) {
        return state;
    }
    return yDoc.transact(() => updatePin(state, yDoc, pinId, (pin) => {
        for (let i = 0; i < pin.pinComponents.length; i++) {
            const updatedComponent = pinComponents.find((component) => component.id === pin.pinComponents[i].id);
            if (updatedComponent) {
                pin.pinComponents[i] = (0, lodash_1.cloneDeep)(updatedComponent); // TODO: ED-305 make updates atomic - selective update component properties, not a full clone
            }
        }
        return pin;
    }));
}
exports.overridePinComponents = overridePinComponents;
/**
 * The provided pin component settings will be cloned before being assigned to the pin
 */
function updatePinComponentSettings(state, yDoc, pinId, pinComponentId, settings) {
    return yDoc.transact(() => updatePin(state, yDoc, pinId, (pin) => {
        updatePinComponent(pin, pinComponentId, (component) => {
            const componentSettings = component.settings;
            Object.keys(settings).forEach((key) => {
                componentSettings[key] = (0, lodash_1.cloneDeep)(settings[key]); // TODO: ED-305 make updates atomic - selective update component properties, not a full clone
            });
        });
        return pin;
    }));
}
exports.updatePinComponentSettings = updatePinComponentSettings;
/**
 * The provided pin components and layout will be cloned before being assigned to the pin
 * The pin components array will be updated in place
 */
function updatePinLayout(state, yDoc, pinId, toBeAddedPinComponents, toBeRemovedPinComponentIds, updatedPinComponents, newLayoutDict, layout) {
    return yDoc.transact(() => updatePin(state, yDoc, pinId, (pin) => {
        if (!(0, pins_shared_1.isPinWithLayoutParts)(pin))
            return pin;
        inPlaceArrayRemove(pin.pinComponents, (pc) => toBeRemovedPinComponentIds.includes(pc.id));
        for (let i = 0; i < pin.pinComponents.length; i++) {
            const updatedComponent = updatedPinComponents.find((component) => component.id === pin.pinComponents[i].id);
            if (updatedComponent) {
                pin.pinComponents[i] = (0, lodash_1.cloneDeep)(updatedComponent); // TODO: ED-305 make updates atomic - selective update component properties, not a full clone
            }
        }
        pin.pinComponents.push(...toBeAddedPinComponents.map((pc) => (0, lodash_1.cloneDeep)(pc)));
        pin.settings.layout = layout; // TODO: ED-305 make updates atomic - selective update component properties, not a full clone
        pin.settings.layoutParts = newLayoutDict; // TODO: typescript
        return pin;
    }));
}
exports.updatePinLayout = updatePinLayout;
/**
 * Run an in-place pin update function, ensuring that fragments are created or removed as necessary for tiptap content
 */
function updatePin(state, yDoc, pinId, pinUpdate) {
    if (!pinId) {
        return state;
    }
    const pin = state.pins.find((p) => p.id === pinId);
    if (!pin) {
        return state;
    }
    const beforeUpdatePinComponentIds = pin.pinComponents.map((pc) => pc.id);
    const updatedPin = pinUpdate(pin);
    const afterUpdatePinComponentsIds = updatedPin.pinComponents.map((pc) => pc.id);
    const deletedPinComponentIds = (0, lodash_1.difference)(beforeUpdatePinComponentIds, afterUpdatePinComponentsIds);
    (0, y_doc_fragment_helpers_1.deletePinComponentFragments)(yDoc, updatedPin.id, deletedPinComponentIds);
    (0, y_doc_fragment_helpers_1.moveTipTapContentToFragments)(yDoc, updatedPin);
    (0, pins_shared_1.processPinReferenceFields)(updatedPin, ({ componentId }) => {
        if (deletedPinComponentIds.includes(componentId)) {
            return undefined;
        }
        return componentId;
    });
    return state;
}
function updatePinComponent(pin, componentId, pinComponentUpdate) {
    const pinComponent = pin.pinComponents.find((p) => p.id === componentId);
    if (!pinComponent) {
        return;
    }
    pinComponentUpdate(pinComponent);
}
/**
 * Add Pin and update the order if necessary
 * Note that this function WILL mutate the state.pins array in place
 */
function addPinToList(state, pin, afterPinId, endOfPhase) {
    state.pins.push(updatePinOrdersAndReturnNewPin(state.pins, pin, afterPinId, endOfPhase));
    return state;
}
exports.addPinToList = addPinToList;
function updatePinOrdersAndReturnNewPin(pins, pin, afterPinId, endOfPhase) {
    if (!afterPinId && !endOfPhase) {
        if (pins.length > 0) {
            throw new Error('Cannot add pin without afterPinId or endOfPhase if there are pins in the list');
        }
        return setPinStartOrder(pins, pin);
    }
    if (afterPinId) {
        const afterPin = pins.find((pin) => pin.id === afterPinId);
        return !afterPin ? setPinStartOrder(pins, pin) : setPinAfterPhaseOrder(pins, afterPin.order, pin);
    }
    if (endOfPhase) {
        return setPinEndOfPhaseOrder(pins, endOfPhase, pin);
    }
    return pin;
}
function setPinStartOrder(pins, pin) {
    const generator = (0, pins_shared_1.getPinOrderGenerator)(pins.map((pin) => pin.order));
    pin.order = generator.keyStart(pins_shared_1.defaultGroup);
    return pin;
}
function setPinEndOfPhaseOrder(pins, endOfPhase, pin) {
    inPlaceUpdatePinOrderInPhase([pin], pins, endOfPhase);
    return pin;
}
function setPinAfterPhaseOrder(pins, afterPinOrder, pin) {
    inPlaceUpdatePinOrderByPinOrder([pin], pins, afterPinOrder);
    return pin;
}
/**
 * Reorder the pins and update the order
 * The pins in the teacher will use fractional indexing, but here its fine to just update the order for every pin
 * TODO: https://lessonup.atlassian.net/browse/ED-305
 */
function internalReorderPins(pins, pinIds, startOfPhase, afterPinId) {
    const withOutSorted = pins.filter((pin) => !pinIds.includes(pin.id)).sort(pins_shared_1.orderableCompareFn);
    const toInsert = pins.filter((pin) => pinIds.includes(pin.id));
    if (startOfPhase) {
        inPlaceUpdatePinOrderInPhase(toInsert, withOutSorted, startOfPhase, true);
        return pins;
    }
    if (afterPinId) {
        const afterPin = pins.find((pin) => pin.id === afterPinId);
        if (!afterPin)
            return pins;
        inPlaceUpdatePinOrderByPinOrder(toInsert, withOutSorted, afterPin.order);
        return pins;
    }
    return pins;
}
function inPlaceUpdatePinOrderByPinOrder(pinsToInsert, currentPins, pinOrder, before = false) {
    const generator = (0, pins_shared_1.getPinOrderGenerator)(currentPins.map((pin) => pin.order));
    const generatedKeys = before
        ? generator.nKeysBefore(pinOrder, pinsToInsert.length)
        : generator.nKeysAfter(pinOrder, pinsToInsert.length);
    pinsToInsert.forEach((pin, index) => {
        pin.order = generatedKeys[index];
    });
}
function inPlaceUpdatePinOrderInPhase(pinsToInsert, currentPins, phaseNumber, startOfPhase = false) {
    const generator = (0, pins_shared_1.getPinOrderGenerator)(currentPins.map((pin) => pin.order));
    const generatedKeys = startOfPhase
        ? generator.nKeysStart(pinsToInsert.length, phaseNumber)
        : generator.nKeysEnd(pinsToInsert.length, phaseNumber);
    pinsToInsert.forEach((pin, index) => {
        pin.order = generatedKeys[index];
    });
}
function inPlaceArrayRemove(array, condition) {
    for (let i = 0; i < array.length; i++) {
        const item = array[i];
        if (condition(item)) {
            array.splice(i, 1);
            i--;
        }
    }
    return array;
}
//# sourceMappingURL=yjs-lesson.js.map